'use client';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth, type UseAuthResult } from '@/hooks/use-auth';
import { useSessionReplay } from '@/tenants/_shared/lib/session-replay';
import { trackRego } from '../../lib/registration-analytics';
import {
  resolveRegistrationInitState,
  type RegistrationInitState,
} from '../lib/registrationInitState';
import { buildResumeEntrantSummary } from '../lib/resumeEntrant';
import type { RegistrationEntrantSummary } from '../types';

export interface UseRegistrationInitResult {
  isLoading: boolean;
  initialStep: number;
  isRedirectReturn: boolean;
  /** User already has an active registration — show banner with dashboard link */
  isBlocked: boolean;
  pendingRegistration: RegistrationInitState['pendingRegistration'];
  authUser: UseAuthResult['user'];
  shouldSkipAuthStep: boolean;
  registrationStatus: UseAuthResult['registrationStatus'];
  authenticatedProfile: RegistrationInitState['authenticatedProfile'];
  /**
   * Entrant summary hydrated from the authenticated session, for resume paths
   * where the interactive auth step never mounts to set `auth.entrant`.
   */
  resumeEntrant: RegistrationEntrantSummary | null;
}

/**
 * Consolidates registration init logic into a single deterministic code path.
 *
 * Lifted from dgr/register/hooks/useRegistrationInit with one seam change:
 * `isRedirectReturn` is an input instead of an internal usePaymentReturn()
 * call, so each payment mode (Adyen redirect, hosted checkout) supplies its
 * own return detection.
 */
export function useRegistrationInit(input: {
  initialCountryFromUrl: string;
  detectedCountry: string | null | undefined;
  isRedirectReturn: boolean;
}): UseRegistrationInitResult {
  const { initialCountryFromUrl, detectedCountry, isRedirectReturn } = input;
  const searchParams = useSearchParams();
  const {
    user: authUser,
    entrant,
    registrationStatus,
    isHydrating,
  } = useAuth({
    hydrateFromQuery: true,
  });

  // Capture the registration → checkout journey when replay is enabled (env,
  // per tenant). Recording persists through the /donate/checkout handoff.
  useSessionReplay(true);

  // Track rego_started once per mount
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current || isRedirectReturn) return;
    trackedRef.current = true;
    trackRego('rego_started', {
      country_code: initialCountryFromUrl || detectedCountry || undefined,
      is_returning: !!authUser,
      attempt_number: 1,
      is_redirect_return: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolved = resolveRegistrationInitState({
    isHydrating,
    authUser,
    entrant,
    registrationStatus,
    isRedirectReturn,
    urlIntent: searchParams.get('intent'),
  });

  if (resolved.pendingRegistration?.eventId && !resolved.isRedirectReturn && !resolved.isBlocked) {
    trackRego('rego_resume_from_existing', {
      intent: searchParams.get('intent') === 'changeRide' ? 'changeRide' : 'auto',
      has_pending_registration: true,
      has_checkout_state: !!resolved.pendingRegistration.checkoutState,
    });
  }

  return {
    ...resolved,
    authUser,
    registrationStatus,
    resumeEntrant: buildResumeEntrantSummary(entrant, authUser),
  };
}
